import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import type {
  AnalysisData,
  ExtractedAudience,
  ExtractedBrandVoice,
  ExtractedProduct,
  SavedAnalysis,
  StepState,
} from "./types";

const errMessage = (e: unknown): string =>
  e instanceof Error ? e.message : String(e);

const productsLabel = (n: number): string =>
  n === 0 ? "Nenhum produto detectado" : `${n} produto${n > 1 ? "s" : ""} detectado${n > 1 ? "s" : ""}`;

const audiencesLabel = (n: number): string =>
  n === 0 ? "Nenhum público detectado" : `${n} público${n > 1 ? "s" : ""} detectado${n > 1 ? "s" : ""}`;

const brandVoiceLabel = (b: ExtractedBrandVoice | null): string =>
  b ? "Voz da marca detectada" : "Sem sinais claros de voz";

export type AnalysisSteps = {
  products: StepState;
  audiences: StepState;
  brandVoice: StepState;
};

export type UseDocumentAnalysisResult = {
  data: AnalysisData;
  steps: AnalysisSteps;
  running: boolean;
  runAnalysis: () => Promise<void>;
};

const initialSteps: AnalysisSteps = {
  products: { status: "idle", message: "Produtos & ofertas" },
  audiences: { status: "idle", message: "Públicos-alvo" },
  brandVoice: { status: "idle", message: "Voz da marca" },
};

const emptyData: AnalysisData = {
  products: [],
  audiences: [],
  brandVoice: null,
  productsSummary: null,
  wasTruncated: false,
};

/**
 * Orquestra análise do documento.
 * - Hidrata estado a partir de `savedAnalysis` (sem chamar LLM).
 * - Quando rodando, dispara as 3 fases em paralelo (Promise.allSettled).
 * - Persiste o resultado e só desliga `running` depois do save (data-loss-safe).
 */
export function useDocumentAnalysis({
  documentId,
  savedAnalysis,
  autoStart,
  canReanalyze,
}: {
  documentId: number;
  savedAnalysis: SavedAnalysis | null;
  autoStart: boolean;
  canReanalyze: boolean;
}): UseDocumentAnalysisResult {
  const utils = trpc.useUtils();
  const [data, setData] = useState<AnalysisData>(emptyData);
  const [steps, setSteps] = useState<AnalysisSteps>(initialSteps);
  const [running, setRunning] = useState(false);
  const startedRef = useRef(false);

  const extractProductsMut = trpc.intelligence.extractProducts.useMutation();
  const extractAudiencesMut = trpc.intelligence.extractAudiences.useMutation();
  const extractBrandVoiceMut = trpc.intelligence.extractBrandVoice.useMutation();
  const saveAnalysisMut = trpc.intelligence.saveAnalysis.useMutation();

  useEffect(() => {
    if (savedAnalysis) {
      setData({
        products: savedAnalysis.products ?? [],
        audiences: savedAnalysis.audiences ?? [],
        brandVoice: savedAnalysis.brandVoice ?? null,
        productsSummary: savedAnalysis.productsSummary ?? null,
        wasTruncated: !!savedAnalysis.wasTruncated,
      });
      const errors = savedAnalysis.errors ?? {};
      const productsCount = savedAnalysis.products?.length ?? 0;
      const audiencesCount = savedAnalysis.audiences?.length ?? 0;
      setSteps({
        products: errors.products
          ? { status: "error", message: "Falha ao extrair produtos", errorMessage: errors.products }
          : { status: "done", message: productsLabel(productsCount) },
        audiences: errors.audiences
          ? { status: "error", message: "Falha ao extrair públicos", errorMessage: errors.audiences }
          : { status: "done", message: audiencesLabel(audiencesCount) },
        brandVoice: errors.brandVoice
          ? { status: "error", message: "Falha ao extrair voz", errorMessage: errors.brandVoice }
          : { status: "done", message: brandVoiceLabel(savedAnalysis.brandVoice ?? null) },
      });
      return;
    }
    if (autoStart && canReanalyze && !startedRef.current) {
      startedRef.current = true;
      void runAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAnalysis, autoStart, canReanalyze]);

  async function runAnalysis(): Promise<void> {
    setRunning(true);
    setData(emptyData);
    setSteps({
      products: { status: "running", message: "Analisando produtos" },
      audiences: { status: "running", message: "Analisando públicos" },
      brandVoice: { status: "running", message: "Analisando voz" },
    });

    const errors: Record<string, string> = {};
    let productsLocal: ExtractedProduct[] = [];
    let audiencesLocal: ExtractedAudience[] = [];
    let brandVoiceLocal: ExtractedBrandVoice | null = null;
    let productsSummaryLocal: string | null = null;
    let truncated = false;

    const productsPromise = extractProductsMut
      .mutateAsync({ documentId })
      .then((res) => {
        productsLocal = res.products;
        productsSummaryLocal = res.summary ?? null;
        if (res.wasTruncated) truncated = true;
        setData((d) => ({
          ...d,
          products: productsLocal,
          productsSummary: productsSummaryLocal,
          wasTruncated: d.wasTruncated || !!res.wasTruncated,
        }));
        setSteps((s) => ({
          ...s,
          products: { status: "done", message: productsLabel(productsLocal.length) },
        }));
      })
      .catch((err) => {
        errors.products = errMessage(err);
        setSteps((s) => ({
          ...s,
          products: { status: "error", message: "Falha ao extrair produtos", errorMessage: errors.products },
        }));
      });

    const audiencesPromise = extractAudiencesMut
      .mutateAsync({ documentId })
      .then((res) => {
        audiencesLocal = res.audiences;
        if (res.wasTruncated) truncated = true;
        setData((d) => ({
          ...d,
          audiences: audiencesLocal,
          wasTruncated: d.wasTruncated || !!res.wasTruncated,
        }));
        setSteps((s) => ({
          ...s,
          audiences: { status: "done", message: audiencesLabel(audiencesLocal.length) },
        }));
      })
      .catch((err) => {
        errors.audiences = errMessage(err);
        setSteps((s) => ({
          ...s,
          audiences: { status: "error", message: "Falha ao extrair públicos", errorMessage: errors.audiences },
        }));
      });

    const brandVoicePromise = extractBrandVoiceMut
      .mutateAsync({ documentId })
      .then((res) => {
        brandVoiceLocal = res.brandVoice;
        if (res.wasTruncated) truncated = true;
        setData((d) => ({
          ...d,
          brandVoice: brandVoiceLocal,
          wasTruncated: d.wasTruncated || !!res.wasTruncated,
        }));
        setSteps((s) => ({
          ...s,
          brandVoice: { status: "done", message: brandVoiceLabel(brandVoiceLocal) },
        }));
      })
      .catch((err) => {
        errors.brandVoice = errMessage(err);
        setSteps((s) => ({
          ...s,
          brandVoice: { status: "error", message: "Falha ao extrair voz", errorMessage: errors.brandVoice },
        }));
      });

    await Promise.allSettled([productsPromise, audiencesPromise, brandVoicePromise]);

    try {
      await saveAnalysisMut.mutateAsync({
        documentId,
        data: {
          productsSummary: productsSummaryLocal,
          products: productsLocal,
          audiences: audiencesLocal,
          brandVoice: brandVoiceLocal,
          wasTruncated: truncated,
          ...(Object.keys(errors).length > 0 ? { errors } : {}),
        },
      });
      utils.knowledgeBase.list.invalidate();
    } catch (err) {
      console.error("[useDocumentAnalysis] saveAnalysis falhou:", err);
      toast.error(`Falha ao salvar análise: ${errMessage(err)}. Tente reanalisar.`);
    } finally {
      setRunning(false);
    }
  }

  return { data, steps, running, runAnalysis };
}
